import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Link2, Copy, Check, ExternalLink, Loader2, Info, AlertTriangle, RefreshCw } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));

// Owner share controls. Persisted settings (`saved`) are tracked separately from the current
// draft (`enabled`/`selected`); the stable link can only be copied/opened when the draft matches
// what is actually saved. A failed load shows an error+retry and exposes no previous resource's
// settings; editing is locked while saving and fully reset whenever the resource/open changes.
export function ShareDialog({ open, onOpenChange, url, type = "campaign", id }) {
  const isArtist = type === "artist";
  const isSong = type === "song";
  const base = isArtist ? "artists" : isSong ? "songs" : "campaigns";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saved, setSaved] = useState(null);          // persisted snapshot { enabled, selected:Set, legacy }
  const [enabled, setEnabled] = useState(false);      // draft
  const [selected, setSelected] = useState(() => new Set()); // draft
  const [available, setAvailable] = useState([]);
  const [tokenUrl, setTokenUrl] = useState("");       // song: unguessable-token link from backend
  const [copied, setCopied] = useState(false);

  // Song links use an unguessable token path returned by the backend; artist/campaign use the
  // id-based `url` prop.
  const effUrl = isSong ? tokenUrl : url;

  const resetState = () => {
    setLoadError(false);
    setSaveError(false);
    setSaved(null);
    setAvailable([]);
    setEnabled(false);
    setSelected(new Set());
    setTokenUrl("");
    setCopied(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    setSaveError(false);
    try {
      const { data } = await api.get(`/${base}/${id}/share`);
      const sel = new Set(isArtist ? (data.selected_campaign_ids || []) : []);
      setSaved({ enabled: Boolean(data.enabled), selected: sel, legacy: Boolean(data.legacy_unconfigured) });
      setEnabled(Boolean(data.enabled));
      setSelected(new Set(sel));
      if (isArtist) setAvailable(data.available_campaigns || []);
      if (isSong && data.share_url_path) setTokenUrl(`${window.location.origin}${data.share_url_path}`);
    } catch (err) {
      // Never keep a previous resource's settings visible on failure.
      setSaved(null);
      setAvailable([]);
      setEnabled(false);
      setSelected(new Set());
      setTokenUrl("");
      setLoadError(true);
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [base, id, isArtist, isSong]);

  useEffect(() => {
    if (!open || !id) return;
    resetState();
    load();
  }, [open, id, load]);

  const dirty = Boolean(
    saved && (enabled !== saved.enabled || (isArtist && !setsEqual(selected, saved.selected)))
  );
  const ready = !loading && !loadError && saved !== null;
  const canShare = ready && !saving && !dirty && !saveError;
  const canEdit = ready && !saving;
  // A legacy artist starts with an empty selection that isn't "dirty"; still let the owner save it
  // explicitly (converts legacy -> configured). Copy/Open stay driven by `canShare`, so with no
  // draft edits they remain usable.
  const canSave = canEdit && (dirty || (isArtist && Boolean(saved?.legacy)));
  const saveLabel = isArtist && saved?.legacy && !dirty ? "Save selected campaigns" : "Save share settings";

  const toggleCampaign = (cid) => {
    if (!canEdit) return;
    setSaveError(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };
  const selectAll = () => { if (canEdit) { setSaveError(false); setSelected(new Set(available.map((c) => c.id))); } };
  const clearAll = () => { if (canEdit) { setSaveError(false); setSelected(new Set()); } };
  const onToggleEnabled = (v) => { if (canEdit) { setSaveError(false); setEnabled(v); } };

  const save = async () => {
    setSaving(true);
    setSaveError(false);
    try {
      const payload = isArtist ? { enabled, campaign_ids: [...selected] } : { enabled };
      const { data } = await api.post(`/${base}/${id}/share`, payload);
      setSaved({
        enabled: Boolean(data.enabled),
        selected: new Set(isArtist ? (data.selected_campaign_ids || [...selected]) : []),
        legacy: Boolean(data.legacy_unconfigured),
      });
      if (isSong && data.share_url_path) setTokenUrl(`${window.location.origin}${data.share_url_path}`);
      toast.success(enabled ? "Share settings saved" : "Link revoked — settings saved");
    } catch (err) {
      setSaveError(true);
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!canShare) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(effUrl);
      ok = true;
    } catch {
      try {
        const el = document.createElement("textarea");
        el.value = effUrl; document.body.appendChild(el); el.select();
        ok = document.execCommand("copy");
        document.body.removeChild(el);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Couldn't copy — select the link and copy it manually");
    }
  };

  const openLink = () => { if (canShare) window.open(effUrl, "_blank", "noopener,noreferrer"); };
  const kindLabel = isArtist ? "artist rollout" : isSong ? "song rollout" : "campaign";

  const toggleSub = () => {
    if (!saved) return "";
    if (enabled) return "Anyone with the link can view";
    if (saved.enabled) return "Will be revoked once you save";   // draft only — not revoked yet
    return "Link is revoked (returns not-found)";                 // persisted revoked
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share this {kindLabel}</DialogTitle>
          <DialogDescription>
            Anyone with this link can open a read-only version. No login required.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : loadError ? (
          <div data-testid="share-load-error" className="flex flex-col items-center gap-3 py-8 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
            <p className="text-sm font-medium text-[#111]">Couldn't load share settings</p>
            <p className="text-xs text-neutral-500">Nothing was changed. Please try again.</p>
            <Button data-testid="share-retry-btn" onClick={load} variant="outline" className="rounded-full border-neutral-300">
              <RefreshCw className="mr-1.5 h-4 w-4" /> Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <span
              data-testid="share-viewonly-label"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E7DFCC] bg-[#FCF8EF] px-3 py-1 text-xs font-medium text-[#6B7A80]"
            >
              <Eye className="h-3.5 w-3.5" /> View-only · read-only report
            </span>

            {saved?.legacy && (
              <div
                data-testid="share-legacy-note"
                className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
              >
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  This link is using <strong>legacy settings</strong> and stays active as-is
                  {isArtist ? " (currently showing all active campaigns)" : ""}. Your choices below
                  apply only after you press <strong>Save</strong>.
                </span>
              </div>
            )}

            <div className="flex items-center justify-between rounded-xl border border-black/[0.06] bg-neutral-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-[#111]">Link enabled</p>
                <p data-testid="share-toggle-sub" className="text-xs text-neutral-500">{toggleSub()}</p>
              </div>
              <Switch data-testid="share-enabled-switch" checked={enabled} disabled={!canEdit} onCheckedChange={onToggleEnabled} />
            </div>

            {isArtist && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <Label>Campaigns in this share ({selected.size}/{available.length})</Label>
                  <div className="flex gap-2">
                    <button data-testid="share-select-all" onClick={selectAll} disabled={!canEdit} className="text-xs font-medium text-[#35606E] hover:underline disabled:opacity-40">Select all</button>
                    <button data-testid="share-clear-all" onClick={clearAll} disabled={!canEdit} className="text-xs font-medium text-neutral-500 hover:underline disabled:opacity-40">Clear</button>
                  </div>
                </div>
                {available.length === 0 ? (
                  <p className="rounded-xl border border-black/[0.06] bg-neutral-50 p-3 text-xs text-neutral-500">No campaigns under this artist yet.</p>
                ) : (
                  <ScrollArea className="h-48 rounded-xl border border-black/[0.06]">
                    <div className="divide-y divide-black/[0.04]">
                      {available.map((c) => (
                        <label
                          key={c.id}
                          data-testid={`share-campaign-${c.id}`}
                          className={`flex items-center gap-3 px-3 py-2.5 ${canEdit ? "cursor-pointer hover:bg-neutral-50" : "opacity-60"}`}
                        >
                          <Checkbox
                            data-testid={`share-campaign-checkbox-${c.id}`}
                            checked={selected.has(c.id)}
                            disabled={!canEdit}
                            onCheckedChange={() => toggleCampaign(c.id)}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-[#111]">{c.title || "Untitled campaign"}</p>
                            <p className="truncate text-xs text-neutral-500">{c.song_title || "—"} · {c.platform}</p>
                          </div>
                          {c.revoked && <Badge variant="outline" className="shrink-0 border-amber-300 text-[10px] text-amber-700">campaign revoked</Badge>}
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                {selected.size === 0 && (
                  <p data-testid="share-empty-note" className="mt-1.5 text-xs text-neutral-500">
                    Nothing selected — the shared rollout will show no campaign data.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label className="flex items-center gap-1"><Link2 className="h-3 w-3" /> Shareable link</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  data-testid="share-url-input"
                  readOnly
                  value={effUrl}
                  onFocus={(e) => e.target.select()}
                  className="font-mono text-sm"
                />
                <Button data-testid="share-copy-btn" onClick={copy} disabled={!canShare} variant="outline" aria-label="Copy link" title="Copy link" className="shrink-0 rounded-full border-neutral-300">
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button data-testid="share-open-btn" onClick={openLink} disabled={!canShare} variant="outline" aria-label="Open link in new tab" title="Open link in new tab" className="shrink-0 rounded-full border-neutral-300">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              {(dirty || saveError) && (
                <p data-testid="share-unsaved-note" className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {saveError ? "Save failed — changes aren't live. Save again before sharing the link." : "Unsaved changes — save first to share the current link."}
                </p>
              )}
            </div>

            <Button data-testid="share-save-btn" onClick={save} disabled={!canSave} className="w-full rounded-full bg-[#35606E] py-5 hover:bg-[#294b57]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {saveLabel}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
